import {
  create as createModal,
  useModal,
  show as showDrawer,
} from '@ebay/nice-modal-react'
import { Form, Formik } from 'formik'
import { ReactElement, useCallback, useState } from 'react'
import { Translate } from 'src/i18n'
import { Button } from 'src/ui-components'
import CustomField from 'src/ui-components/forms/formik/InputField'
import BaseDrawer from 'src/ui-components/modals/BaseDrawer'
import styled from 'styled-components'
import PartSummary from './Common/PartSummary'
import { EditablePart } from './Common/interfaces'
import { observer } from 'mobx-react-lite'
import { AddToListStore } from 'src/store/lists/AddToListStore'
import { ValidatePart } from 'src/store/lists/interfaces'
import ReviewPartDrawer from './Review'

interface Props {
  listId: number
  group: string
  part: EditablePart
  initialImgUrl?: string
  store: AddToListStore
}

const EditPartDrawer = createModal(
  observer((props: Props): ReactElement => {
    const { listId, group, part, initialImgUrl, store } = props
    const [submitInProgress, setSubmitInProgress] = useState(false)
    const modal = useModal()

    const validatePart = useCallback(
      async (part: EditablePart): Promise<EditablePart[]> => {
        const editedPart: ValidatePart = {
          ...part,
          partNumber: part.partNum,
          stockQty: part.qty,
          groupName: part.group,
          comment: part.comments,
          originalSeqNo: part.seqno,
        }
        const originalSeqno = part.seqno
        const validatedParts = await store.validateTransientItems(
          listId,
          part.group,
          [editedPart]
        )

        const editableParts: EditablePart[] = validatedParts.map(
          (p): EditablePart => {
            return { ...p, partId: originalSeqno.toString() }
          }
        )

        return editableParts
      },
      [store, listId]
    )

    // TODO: api integration
    const handleOnSubmit = async (editedPart: EditablePart): Promise<void> => {
      try {
        setSubmitInProgress(true)
        const validatedParts = await validatePart(editedPart)
        setSubmitInProgress(false)

        modal.remove()
        showDrawer(ReviewPartDrawer, {
          listId,
          group,
          newParts: validatedParts,
          oldPart: part,
          store,
        })
      } catch (_e: unknown) {
        setSubmitInProgress(false)
      }
    }

    const body = (
      <FlexColumn>
        <PartSummary part={part} initialImgUrl={initialImgUrl} />

        <Formik initialValues={{ ...part }} onSubmit={handleOnSubmit}>
          {(props) => (
            <Form>
              <InputFieldsGap>
                <CustomField
                  name="lineCode"
                  value={props.values.lineCode}
                  label={Translate('lineCode')}
                  type="text"
                  inputStyle={{ width: '105px' }}
                  wrapperStyle={{ margin: '0px' }}
                />

                <CustomField
                  name="partNum"
                  value={props.values.partNum}
                  label={Translate('partNum')}
                  type="text"
                  inputStyle={{ width: '209px' }}
                  wrapperStyle={{ margin: '0px' }}
                />

                <CustomField
                  name="qty"
                  value={props.values.qty?.toString()}
                  label={Translate('qty')}
                  type="number"
                  inputStyle={{ width: '105px' }}
                  wrapperStyle={{ margin: '0px' }}
                />
              </InputFieldsGap>

              <CustomField
                name="group"
                value={props.values.group}
                label={Translate('group')}
                type="text"
                wrapperStyle={{ margin: '0px 0px 19px 0px' }}
              />

              <CustomField
                label={Translate('comments')}
                name="comments"
                type="textarea"
                wrapperStyle={{ width: '100%', margin: '0px' }}
                inputStyle={{ height: '96px' }}
                maxSize={45}
              />

              <ButtonsContainer>
                <Button
                  type="reset"
                  text="cancel"
                  variant="secondary"
                  onClick={modal.remove}
                />
                <Button
                  type="submit"
                  text="validate"
                  loading={submitInProgress}
                />
              </ButtonsContainer>
            </Form>
          )}
        </Formik>
      </FlexColumn>
    )

    return (
      <BaseDrawer
        title={'editItem'}
        showHeaderDivider
        body={body}
        open={modal.visible}
        onClose={modal.remove}
        width="544px"
      />
    )
  })
)

const Flex = styled.div`
  display: flex;
`

const FlexColumn = styled(Flex)`
  flex-direction: column;
`

const InputFieldsGap = styled(Flex)`
  gap: 30.5px;
  margin-bottom: 19px;
`

const ButtonsContainer = styled(Flex)`
  margin-top: 24px;
  justify-content: end;
  gap: 16px;
`

export default EditPartDrawer
